import { $isLinkNode } from '@lexical/link';
import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Button } from '@talentmesh/ui/components/button';
import { cn } from '@talentmesh/ui/lib/utils';
import {
    $getSelection,
    $isParagraphNode,
    $isRangeSelection,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { RemoveFormatting } from 'lucide-react';
import { useEffect, useEffectEvent, useState } from 'react';
import { SupportedFeatures, SupportedListNodeTagType, SupportedTextFormatType } from '../../utils/features';
import { getSelectedNode } from '../../utils/lexical';
import BlockFormatMenuButton, { BlockFormatMenuButtonProps } from './components/BlockFormatMenuButton';
import ToggleButton from './components/ToggleButton';
import ToolbarSeparator from './components/ToolbarSeparator';
import useToggleActions from './hooks/useToggleActions';
import { clearFormatting } from './utils';

export { ToolbarSeparator };

export interface ToolbarPluginProps {
    disabled?: boolean;
    features?: SupportedFeatures[];
    className?: string;
}

const ToolbarPlugin = ({ features, disabled, className, children }: React.PropsWithChildren<ToolbarPluginProps>) => {
    const [editor] = useLexicalComposerContext();
    const { formats, lists } = useToggleActions(features);

    const [blockStylings, setBlockStylings] = useState<Set<string>>(new Set<string>());
    const [blockType, setBlockType] = useState<BlockFormatMenuButtonProps['tag']>(undefined);

    const updateToolbar = useEffectEvent(() => {
        if (disabled) {
            return;
        }

        const selection = $getSelection();
        const newBlockStylings = new Set<string>();

        if ($isRangeSelection(selection)) {
            formats.forEach(({ style }) => newBlockStylings[selection.hasFormat(style) ? 'add' : 'delete'](style));

            const anchorNode = selection?.anchor?.getNode();
            const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);

            if (elementDOM !== null) {
                // if text highlighted is part of list node,
                // check for each provided whether to add or delete
                // otherwise, it's a normal text
                // (update the else part if you want to add heading, or any other type of text block)
                // check out https://codesandbox.io/s/lexical-rich-text-example-5tncvy?file=/src/plugins/ToolbarPlugin.js
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                    const type = parentList ? parentList.getTag() : element.getTag();

                    lists.forEach(({ style }) => newBlockStylings[style === type ? 'add' : 'delete'](style));
                } else if ($isHeadingNode(element)) {
                    setBlockType('h4');
                } else if ($isParagraphNode(element)) {
                    setBlockType('p');
                } else {
                    setBlockType(undefined);
                }

                const node = getSelectedNode(selection);
                const parent = node.getParent();
                if ($isLinkNode(parent) || $isLinkNode(node)) {
                    newBlockStylings.add('link');
                } else {
                    newBlockStylings.delete('link');
                }
            }
        }

        setBlockStylings(newBlockStylings);
    });

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor]);

    const toggleFormat = (style: SupportedTextFormatType) => {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, style);
    };

    const toggleListType = (style: SupportedListNodeTagType) => {
        const listTypeToExecute = style === 'ol' ? INSERT_ORDERED_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND;
        editor.dispatchCommand(blockStylings.has(style) ? REMOVE_LIST_COMMAND : listTypeToExecute);
    };

    return (
        <div
            id="talentmesh-rte-toolbar"
            className={cn(
                'relative flex flex-wrap items-center gap-0.5',
                'border-border rounded-t-xl border border-b-0',
                'bg-muted px-1.5 py-1',
                className,
            )}
        >
            {features?.includes('headingStyle') && <BlockFormatMenuButton disabled={disabled} tag={blockType} />}

            {features?.includes('headingStyle') && formats.length > 0 && <ToolbarSeparator />}

            {formats.map((item) => (
                <ToggleButton
                    key={item.style}
                    title={item.label}
                    value={item.style}
                    pressed={blockStylings.has(item.style)}
                    onClick={() => toggleFormat(item.style)}
                    disabled={disabled}
                >
                    {item.icon}
                </ToggleButton>
            ))}

            {formats.length > 0 && (
                <Button
                    className="text-muted-foreground"
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    title="Clear formatting"
                    onClick={() => clearFormatting(editor)}
                    disabled={disabled}
                >
                    <RemoveFormatting className="size-4" />
                </Button>
            )}

            {lists.length > 0 && <ToolbarSeparator />}
            {lists.map((item) => (
                <ToggleButton
                    key={item.style}
                    title={item.label}
                    value={item.style}
                    pressed={blockStylings.has(item.style)}
                    onClick={() => toggleListType(item.style)}
                    disabled={disabled}
                >
                    {item.icon}
                </ToggleButton>
            ))}

            {children}
        </div>
    );
};

export default ToolbarPlugin;
